import Icon, {
  DownOutlined,
  LeftOutlined,
  LoadingOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { css, cx } from '@emotion/css';
import { useQuery } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import { Divider, Popover, Tooltip } from 'antd';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { modeTranslate, TranslateMode } from 'services/api/overseaTranslate';
import { eventBus } from 'services/eventBus';
import { ReactComponent as TranslateSvg } from 'views/Conversation/assets/translate.svg';
import { ReactComponent as GoogleSvg } from 'views/Conversation/assets/google.svg';
import { ReactComponent as GptSvg } from 'views/Conversation/assets/gpt.svg';
import { ReactComponent as DeepseekSvg } from 'views/Conversation/assets/deepseek.svg';
import { ReactComponent as ChatPlusAISvg } from 'views/Conversation/assets/chatplusai.svg';
import { useState } from 'react';
import robotAvatar from './assets/robot.png';
import {
  getRangeTimestamp,
  QAPairQuerySelectLimit,
  useQAPairSelectMode,
  useSetQAPairMeta,
} from 'hooks/business/im/useQAPair';
import { toNumber } from 'lodash-es';
import { FingerIcon } from './FingerIcon';
import { useClearGlobalSelectedState } from 'hooks/business/im/useMessageSelect';

interface ReWriteQueryProps {
  knowledgeQuery: Conversation.KnowledgeQueryMessage;
}

const translateModes = [
  { title: 'Google', mode: 'GOOGLE', icon: GoogleSvg },
  { title: 'ChatGPT', mode: 'CHAT_GPT', icon: GptSvg },
  { title: 'DeepSeek', mode: 'DEEPSEEK', icon: DeepseekSvg },
  { title: 'ChatPlusAI', mode: 'CHATPLUSAI', icon: ChatPlusAISvg },
] as const;

export const ReWriteQuery = (props: ReWriteQueryProps) => {
  const { knowledgeQuery } = props;
  const originalText = knowledgeQuery?.rewriteQuery || knowledgeQuery?.textContent?.text;
  const systemLanguage = useSystemLanguageValue();
  const { t } = useTranslate();
  const setQAPairMeta = useSetQAPairMeta();
  const qaPairSelectMode = useQAPairSelectMode();

  const clearGlobalSelectedState = useClearGlobalSelectedState();

  const [translateMode, setTranslateMode] = useState<TranslateMode>('CHATPLUSAI');

  const {
    data: translationContent,
    isFetching,
    refetch,
  } = useQuery({
    enabled: !!originalText,
    queryKey: ['knowledgeQueryTranslation', originalText!, translateMode],
    queryFn: async () => {
      const content = await modeTranslate(originalText!, systemLanguage, translateMode);
      return content;
    },
  });

  if (qaPairSelectMode === 'multiple') {
    return (
      <div className="max-w-[500px] min-w-[200px] flex items-center gap-2 min-h-[56px] bg-[var(--fett-bg-elevated)] px-1 pointer-events-auto">
        <span
          className="cursor-pointer text-xs text-[#777777] p-2"
          onClick={() => {
            setQAPairMeta((prev) => {
              const { rangeTimestamp, defaultKnowledgeQueryMessage } = prev;
              const newRangeTimestamp = defaultKnowledgeQueryMessage?.sendTime
                ? toNumber(defaultKnowledgeQueryMessage?.sendTime)
                : rangeTimestamp;
              return {
                ...prev,
                selectMode: 'single',
                rangeTimestamp: newRangeTimestamp,
              };
            });
            clearGlobalSelectedState();
          }}
        >
          <LeftOutlined />
        </span>
        <FingerIcon />
        <div>{t('请选择一类问题（至多可选x句）', { x: QAPairQuerySelectLimit })}</div>
      </div>
    );
  }

  const popoverContent = (
    <div className="flex items-center gap-2">
      {translateModes.map(({ title, mode, icon }) => (
        <Tooltip
          key={mode}
          getTooltipContainer={(_) => _}
          title={t('ViewsChat.使用x翻译', { x: title })}
        >
          <div
            className="cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              setTranslateMode(mode);
            }}
          >
            <Icon component={icon} />
          </div>
        </Tooltip>
      ))}
    </div>
  );

  const handleSelectQuestion = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    eventBus.emit('CLEAR_QAPAIR_COUNTDOWN_TIMER');
    setQAPairMeta((prev) => {
      const { rangeTimestamp, defaultKnowledgeQueryMessage } = prev;
      const newRangeTimestamp = defaultKnowledgeQueryMessage?.sendTime
        ? getRangeTimestamp(defaultKnowledgeQueryMessage?.sendTime)
        : rangeTimestamp;
      return { ...prev, selectMode: 'multiple', rangeTimestamp: newRangeTimestamp };
    });
  };

  return (
    <div
      className={cx(
        'max-w-[500px] min-w-[200px] bg-[var(--fett-bg-elevated)] border border-solid border-[var(--fett-fill-300)] rounded p-2 flex gap-2 pointer-events-auto',
        {
          'max-h-[160px] overflow-y-auto': window.innerHeight < 800,
          'max-h-[320px] overflow-y-auto': window.innerHeight >= 800 && window.innerHeight < 1080,
          'cursor-pointer': !!knowledgeQuery?.msgId,
        },
      )}
      onClick={() => {
        if (knowledgeQuery?.msgId) {
          eventBus.emit('SCROLL_TO_MSG_BY_ID', knowledgeQuery.msgId);
        }
      }}
    >
      <img className="w-[32px] h-[32px]" src={robotAvatar} alt="robot" />
      <div className="flex-1">
        <div className="flex items-center justify-between flex-nowrap text-sm mb-2 gap-4">
          <div className="text-[var(--fett-text-tertiary)]">{t('AI总结买家问题')}</div>
          <div
            className="!p-0 text-[var(--fett-scenario-accent-text)] cursor-pointer hover:opacity-80"
            onClick={handleSelectQuestion}
          >
            {t('自主选择问题')}
          </div>
        </div>
        <div>{originalText}</div>
        <Divider className="!my-2" />
        <div
          className="text-[var(--fett-text-tertiary)] flex items-start"
          onClick={(e) => e.stopPropagation()}
        >
          {isFetching ? (
            <span className="text-[#85b1ff] flex items-center gap-1">
              <LoadingOutlined />
              {t('Entities.翻译中')}
            </span>
          ) : translationContent ? (
            <div className="flex-1">{translationContent}</div>
          ) : (
            <span
              className="text-[var(--fett-scenario-accent-text)] flex items-center gap-1"
              onClick={() => refetch()}
            >
              <ReloadOutlined className="!text-[10px]" />
              {t('Entities.翻译失败')}
            </span>
          )}
          {!isFetching && !!translationContent && (
            <Popover
              overlayClassName={css`
                .ant-popover-inner-content {
                  padding-top: 4px;
                  padding-bottom: 4px;
                  padding-right: 8px;
                  padding-left: 8px;
                }
              `}
              placement="bottom"
              content={popoverContent}
            >
              <div className="h-[22px] flex items-center justify-center">
                <Icon component={TranslateSvg} />
                <DownOutlined className="!text-[10px]" />
              </div>
            </Popover>
          )}
        </div>
      </div>
    </div>
  );
};
